import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import {
  useCreateCompanyMutation,
  useGetCompanyQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { ICompanyDetails } from "../../../Api/Interface/api.interface";
import { useStyles } from "../../../theme/appStyles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { uploadImage } from "../../../Components/UploadFile/upload";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useNavigate, useParams } from "react-router-dom";
import {
  validateEmail,
  validatePhoneNumber,
} from "../../../Components/Validation/validationUtils";
import CustomSwitch from "../../../Components/Switch/CustomSwitch";

const Company = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { id: CompanyId } = useParams<{ id: string }>();

  useEffect(() => {}, [CompanyId]);

  const {
    data,
    isLoading: getcompanyIsLoading,
    isError,
    refetch,
  } = useGetCompanyQuery({
    searchText: "",
    id: String(CompanyId),
  });

  const CompanyData = useMemo(() => {
    if (data && Array.isArray(data.data) && data.data.length > 0) {
      return data.data[0] as ICompanyDetails;
    }
    return null;
  }, [data]);

  const [selectedImage, setSelectedImage] = useState<File | undefined>(
    undefined
  );
  const [selectedWatermarkImage, setSelectedWatermarkImage] = useState<File | undefined>(
    undefined
  );
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [newCompany, { isLoading }] = useCreateCompanyMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const navigate = useNavigate();
  const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: CompanyData?.id,
      companyName: CompanyData?.companyName || "",
      companySlog: CompanyData?.companySlog || "",
      companyProvince: CompanyData?.companyProvince || "",
      companyCity: CompanyData?.companyCity || "",
      companyPostalCode: CompanyData?.companyPostalCode || "",
      companyStreetNumber: CompanyData?.companyStreetNumber || "",
      companyStreetName: CompanyData?.companyStreetName || "",
      companyPaymentMethod: CompanyData?.companyPaymentMethod || "",
      companyEmail: CompanyData?.companyEmail || "",
      companyMobileNumber: CompanyData?.companyMobileNumber || "",
      companyAltMobileNumber: CompanyData?.companyAltMobileNumber || "",
      companyWebSiteLink: CompanyData?.companyWebSiteLink || "",
      companyHstNo: CompanyData?.companyHstNo || "",
      companyDescription: CompanyData?.companyDescription || "",
      companyFooterDetails: CompanyData?.companyFooterDetails || "",
      CompanyLogo: CompanyData?.CompanyLogo || "",
      CompanyWatermark: CompanyData?.CompanyWatermark || "",
      isDefault: CompanyData?.isDefault || false,
     openingHours: typeof CompanyData?.openingHours === "string"
  ? JSON.parse(CompanyData.openingHours)
  : [
      { open: "", close: "" }, // Monday
      { open: "", close: "" }, // Tuesday
      { open: "", close: "" }, // Wednesday
      { open: "", close: "" }, // Thursday
      { open: "", close: "" }, // Friday
      { open: "", close: "" }, // Saturday
      { open: "", close: "" }, // Sunday
    ],


    },
    validate: (values) => {
      const errors: { [key: string]: string } = {};

      const emailError = validateEmail(values.companyEmail);
      if (emailError) {
        errors.companyEmail = emailError;
      }

      const mobileNumberError = validatePhoneNumber(values.companyMobileNumber);
      if (mobileNumberError) {
        errors.companyMobileNumber = mobileNumberError;
      }

      const altMobileNumberError = validatePhoneNumber(
        values.companyAltMobileNumber
      );
      if (altMobileNumberError) {
        errors.companyAltMobileNumber = altMobileNumberError;
      }

      return errors;
    },
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        let CompanyLogo: string | undefined = CompanyData?.CompanyLogo;
        let CompanyWatermark: string | undefined = CompanyData?.CompanyWatermark;

        if (selectedImage) {
          setIsImageUploading(true);
          formData.append("CompanyLogo", selectedImage);
          CompanyLogo = (await uploadImage(selectedImage)) || "";
          setIsImageUploading(false);
        }

        if (selectedWatermarkImage) {
          setIsImageUploading(true);
          formData.append("CompanyWatermark", selectedWatermarkImage);
          CompanyWatermark = (await uploadImage(selectedWatermarkImage)) || "";
          setIsImageUploading(false);
        }

        const temData = {
          id: values.id,
          companyName: values.companyName,
          companySlog: values.companySlog,
          companyProvince: values.companyProvince,
          companyCity: values.companyCity,
          companyPostalCode: values.companyPostalCode,
          companyStreetNumber: values.companyStreetNumber,
          companyStreetName: values.companyStreetName,
          companyPaymentMethod: values.companyPaymentMethod,
          companyEmail: values.companyEmail,
          companyMobileNumber: values.companyMobileNumber,
          companyAltMobileNumber: values.companyAltMobileNumber,
          companyWebSiteLink: values.companyWebSiteLink,
          companyDescription: values.companyDescription,
          companyFooterDetails: values.companyFooterDetails,
          companyHstNo: values.companyHstNo,
          isDefault: values.isDefault,
          CompanyLogo,
          CompanyWatermark,
          openingHours: values.openingHours,
        };

        const addCompanyResponse = await newCompany(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          navigate("/company/list");
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return formik.values.companyName === "" ||
      formik.values.companyName === undefined
      ? false
      : true;
  }, [formik]);

  const resizeImage = (
    file: File,
    maxWidth: number,
    maxHeight: number
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let width = img.width;
          let height = img.height;

          // Calculate the new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile);
            } else {
              reject(new Error("Canvas is empty"));
            }
          }, file.type);
        };

        img.onerror = (error) => reject(error);
      };
    });
  };

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif",  "image/webp"];
      const maxSize = 2048 * 1024;

      if (!validTypes.includes(file.type)) {
        showErrorMessage("The file must be an image (jpeg, png, jpg, gif, webp).");
        setSelectedImage(undefined);
      } else if (file.size > maxSize) {
        showErrorMessage("The file must be smaller than 2 MB.");
        setSelectedImage(undefined);
      } else {
        try {
          const resizedImage = await resizeImage(file, 500, 500);
          setSelectedImage(resizedImage);
        } catch (error) {
          showErrorMessage("Failed to resize the image.");
          setSelectedImage(undefined);
        }
      }
    } else {
      setSelectedImage(undefined);
    }
  };

  const handleWatermarkImageChange = async (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const files = event.target.files;
  
      if (files && files.length > 0) {
        const file = files[0];
        const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif", "image/webp"];
        const maxSize = 2048 * 1024;
  
        if (!validTypes.includes(file.type)) {
          showErrorMessage("The file must be an image (jpeg, png, jpg, gif, webp).");
          setSelectedWatermarkImage(undefined);
        } else if (file.size > maxSize) {
          showErrorMessage("The file must be smaller than 2 MB.");
          setSelectedImage(undefined);
        } else {
          try {
            const resizedImage = await resizeImage(file, 500, 500);
            setSelectedWatermarkImage(resizedImage);
          } catch (error) {
            showErrorMessage("Failed to resize the image.");
            setSelectedWatermarkImage(undefined);
          }
        }
      } else {
        setSelectedWatermarkImage(undefined);
      }
    };

  return (
    <Grid container spacing={3}>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <Card className={classes.stepperCard}>
          <Grid
            container
            justifyContent={"space-between"}
            spacing={5}
            sx={{ borderBottom: 2, padding: 2 }}
            className={classes.titleGrid}
          >
            <Grid
              item
              lg={11}
              md={11}
              sm={11}
              xs={11}
              pt={2}
              alignContent={"center"}
            >
              <Typography variant="h2">{CompanyData?.companyName}</Typography>
            </Grid>
            <Grid
              item
              lg={1}
              md={1}
              sm={1}
              xs={1}
              pt={2}
              alignContent={"center"}
            >
              <Button
                variant="outlined"
                endIcon={<ArrowForwardIosIcon />}
                className={classes.addButton}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Add Company Logo
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={2} p={"24px"}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Box className={classes.imgBox}>
                      {selectedImage && (
                        <img
                          src={URL.createObjectURL(selectedImage)}
                          alt="Uploaded"
                          className={classes.SelectImageBox}
                        />
                      )}
                      {!selectedImage &&
                      CompanyData &&
                      CompanyData.CompanyLogo ? (
                        <img
                          alt="Selected"
                          height="160px"
                          src={`${LARAVEL_API_URL}/${CompanyData.CompanyLogo}`}
                          style={{ position: "inherit" }}
                          className={classes.SelectImageBox}
                        />
                      ) : (
                        <></>
                      )}
                      <div>
                        <input
                          accept="image/*"
                          style={{ display: "none" }}
                          id="icon-button-file-1"
                          type="file"
                          onChange={handleImageChange}
                        />
                        <div
                          style={{
                            textAlign: "center",
                          }}
                        >
                          <label htmlFor="icon-button-file-1">
                            <IconButton
                              color="primary"
                              aria-label="upload picture"
                              component="span"
                            >
                              <CloudUploadIcon sx={{ fontSize: 45 }} />
                            </IconButton>
                          </label>

                          <div>PNG, JPG and JPEG are allowed</div>
                        </div>
                      </div>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Company Details
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={3} p={"24px"}>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                          Company Name
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Enter Company Name"
                          size="small"
                          {...formik.getFieldProps("companyName")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                    <Grid item lg={6} md={6} sm={12} xs={12}>
                                      <Grid container alignItems="center">
                                        <Grid item lg={12} md={12} sm={12} xs={12}>
                                          <Typography variant="subtitle1">
                                             Slog
                                          </Typography>
                                        </Grid>
                                        <Grid item lg={12} md={12} sm={12} xs={12}>
                                          <TextField
                                            placeholder="Enter Slog"
                                            size="small"
                                            {...formik.getFieldProps("companySlog")}
                                            className={classes.textField}
                                            InputProps={{
                                              sx: {
                                                fontSize: 14,
                                              },
                                            }}
                                            InputLabelProps={{
                                              sx: {
                                                fontSize: 14,
                                              },
                                            }}
                                          />
                                        </Grid>
                                      </Grid>
                                    </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                          Website Link
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Enter Website Link"
                          size="small"
                          {...formik.getFieldProps("companyWebSiteLink")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                                      <Grid container alignItems="center">
                                        <Grid item lg={12} md={12} sm={12} xs={12}>
                                          <Typography variant="subtitle1">
                                             HST No.
                                          </Typography>
                                        </Grid>
                                        <Grid item lg={12} md={12} sm={12} xs={12}>
                                          <TextField
                                            placeholder="Enter HST No."
                                            size="small"
                                            {...formik.getFieldProps("companyHstNo")}
                                            className={classes.textField}
                                            InputProps={{
                                              sx: {
                                                fontSize: 14,
                                              },
                                            }}
                                            InputLabelProps={{
                                              sx: {
                                                fontSize: 14,
                                              },
                                            }}
                                          />
                                        </Grid>
                                      </Grid>
                                    </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Default</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <CustomSwitch
                          color="primary"
                          sx={{ mr: 2 }}
                          formik={formik}
                          name="isDefault"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                           Description
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Enter Description"
                          size="small"
                          {...formik.getFieldProps("companyDescription")}
                          className={classes.discriptionTextField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          multiline
                          rows={8}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                           Footer Details
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Enter Footer details"
                          size="small"
                          {...formik.getFieldProps("companyFooterDetails")}
                          className={classes.discriptionTextField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          multiline
                          rows={8}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                </Grid>
              </Card>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Company Address
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={3} p={"24px"}>

                <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                           Street No.
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Enter Street No."
                          size="small"
                          {...formik.getFieldProps("companyStreetNumber")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                           Street Name
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Enter Street Name"
                          size="small"
                          {...formik.getFieldProps("companyStreetName")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                           City
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Enter City"
                          size="small"
                          {...formik.getFieldProps("companyCity")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                           Province
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Enter  Province"
                          size="small"
                          {...formik.getFieldProps("companyProvince")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                           Postal Code
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Enter Postal Code"
                          size="small"
                          {...formik.getFieldProps("companyPostalCode")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                
                </Grid>
              </Card>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Company Contact Details
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={3} p={"24px"}>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                          Email
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Enter Email"
                          size="small"
                          {...formik.getFieldProps("companyEmail")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          error={
                            formik.touched.companyEmail &&
                            Boolean(formik.errors.companyEmail)
                          }
                          helperText={
                            formik.touched.companyEmail &&
                            formik.errors.companyEmail
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                           Mobile Number
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Enter Mobile Number"
                          size="small"
                          {...formik.getFieldProps("companyMobileNumber")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          error={
                            formik.touched.companyMobileNumber &&
                            Boolean(formik.errors.companyMobileNumber)
                          }
                          helperText={
                            formik.touched.companyMobileNumber &&
                            formik.errors.companyMobileNumber
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                           Alternate Mobile Number
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Enter Alternate Mobile Number"
                          size="small"
                          {...formik.getFieldProps("companyAltMobileNumber")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          error={
                            formik.touched.companyAltMobileNumber &&
                            Boolean(formik.errors.companyAltMobileNumber)
                          }
                          helperText={
                            formik.touched.companyAltMobileNumber &&
                            formik.errors.companyAltMobileNumber
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>


            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Add Watermark 
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={2} p={"24px"}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Box className={classes.imgBox}>
                      {selectedWatermarkImage && (
                        <img
                          src={URL.createObjectURL(selectedWatermarkImage)}
                          alt="Uploaded"
                          className={classes.SelectImageBox}
                        />
                      )}
                      {!selectedImage &&
                      CompanyData &&
                      CompanyData.CompanyWatermark ? (
                        <img
                          alt="Selected"
                          height="160px"
                          src={`${LARAVEL_API_URL}/${CompanyData.CompanyWatermark}`}
                          style={{ position: "inherit" }}
                          className={classes.SelectImageBox}
                        />
                      ) : (
                        <></>
                      )}
                      <div>
                        <input
                          accept="image/*"
                          style={{ display: "none" }}
                          id="icon-button-file-2"
                          type="file"
                          onChange={handleWatermarkImageChange}
                        />
                        <div
                          style={{
                            textAlign: "center",
                          }}
                        >
                          <label htmlFor="icon-button-file-2">
                            <IconButton
                              color="primary"
                              aria-label="upload picture"
                              component="span"
                            >
                              <CloudUploadIcon sx={{ fontSize: 45 }} />
                            </IconButton>
                          </label>

                          <div>PNG, JPG and JPEG are allowed</div>
                        </div>
                      </div>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Grid>


            {/* <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
              
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid item lg={12} md={12} sm={12} xs={12} pt={2} alignContent={"center"}>
                    <Typography className={classes.cardTitle}>
                      Opening Hours
                    </Typography>
                  </Grid>
                </Grid>
            
              
                <Grid container spacing={3} p={"24px"}>
                  {[
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                  ].map((day, index) => {
                    return (
                      <Grid
                        container
                        item
                        spacing={2}
                        key={day}
                        alignItems="center"
                        sx={{
                          borderBottom: index !== 6 ? "1px solid #eee" : "none",
                          paddingBottom: "12px",
                          marginBottom: "12px",
                        }}
                      >
                      
                        <Grid item xs={12} sm={3}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {day}
                          </Typography>
                        </Grid>
            
                     
                        <Grid item xs={6} sm={4}>
                          <TextField
                            type="time"
                            size="small"
                            fullWidth 
                            {...formik.getFieldProps(`openingHours[${index}].open`)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
            
                       
                        <Grid item xs={6} sm={4}>
                          <TextField
                            type="time"
                            size="small"
                            fullWidth
                            {...formik.getFieldProps(`openingHours[${index}].close`)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </Grid>
                    );
                  })}
                </Grid>
              </Card>
            </Grid> */}

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid container spacing={3} p={"24px"}>
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <Button
                      variant="contained"
                      startIcon={<SaveAltIcon />}
                      className={classes.addButton}
                      onClick={() => formik.handleSubmit()}
                      disabled={!formValid}
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
};

export default Company;
